import React, { useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useContext, useEffect } from 'react';
import { MobxContext } from 'shared/lib/contexts/mobxContext';
import Breadcrumbs from 'shared/lib/components/controls/breadcrumbs/breadcrumbs';
import { useNavigate } from 'react-router-dom';
import BreadcrumbsItem from 'shared/lib/components/controls/breadcrumbs/breadcrumbsItem';
import PageWrapper from 'shared/lib/components/containers/pageWrapper';
import ColumnCenterFlex from 'shared/lib/components/containers/columnCenterFlex';
import { Box, Typography } from '@mui/material';
import PagePaddingWrapper from 'shared/lib/components/containers/pagePaddingWrapper';
import PageInnerContainer from 'pages/components/pageInnerContainer';
import { useTranslation } from 'react-i18next';
import { TrashModel } from 'entities/trash/model/trashModel';
import RestoreAllModal from 'widgets/restore/restoreAllModal';
import { ExplorerEntityTypes } from 'entities/types';
import WarningModal from 'shared/lib/components/modals/warningModal';
import { UiContext } from 'shared/lib/contexts/uiContext';
import { AxiosError } from 'axios';
import { BackErrors } from 'shared/lib/utils/globalUtils';
import ErrorModal from 'shared/lib/components/modals/errorModal';
import map from 'lodash-es/map';
import TrashContentGridController from 'widgets/grids/trashContentGridController';
import TrashPageHeader from 'pages/trash/trashPageHeader';
import { trashApi } from 'entities/trash/api/trashApi';
import { axiosApi } from 'app/api/api';

export default observer(function TrashPage() {
  const { mobxStore } = useContext(MobxContext);
  const {
    spacings: { sp },
  } = useContext(UiContext);

  const navigate = useNavigate();
  const { t } = useTranslation();

  const [model] = useState(() => new TrashModel({ rootStore: mobxStore }));

  const [isRestoreAll, setIsRestoreAll] = useState(false);
  const [isDeleteAll, setIsDeleteAll] = useState(false);
  const [isDublicateError, setIsDublicateError] = useState(false);

  useEffect(() => {
    model.fetchContent();
  }, [model]);

  return (
    <PageWrapper>
      <PagePaddingWrapper
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box>
          <Breadcrumbs>
            <BreadcrumbsItem
              onClick={() => {
                navigate('/', { replace: false });
              }}
              title={t('breadcrumbs.main')}
            >
              {t('breadcrumbs.main')}
            </BreadcrumbsItem>
            <BreadcrumbsItem title={t('common.trash')}>
              {t('common.trash')}
            </BreadcrumbsItem>
          </Breadcrumbs>
        </Box>

        <ColumnCenterFlex sx={{ flexGrow: 1 }}>
          <PageInnerContainer>
            <TrashPageHeader
              onRestore={() => setIsRestoreAll(true)}
              onClear={() => setIsDeleteAll(true)}
            ></TrashPageHeader>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: sp.s20,
                flexGrow: 1,
              }}
            >
              <TrashContentGridController
                navigatePathname="/trash_explorer"
                items={model.content}
                supportTypes={[
                  ExplorerEntityTypes.FOLDER,
                  ExplorerEntityTypes.FILE,
                  ExplorerEntityTypes.TABLE,
                ]}
                afterFolderRestore={(folderModel) =>
                  model.localDeleteFolders([folderModel.id])
                }
                afterFolderFinallyDelete={(id) =>
                  model.localDeleteFolders([id])
                }
                afterFileRestore={(folderModel) =>
                  model.localDeleteFiles([folderModel.id])
                }
                afterFileDelete={(id) => model.localDeleteFiles([id])}
                afterTableRestore={(folderModel) =>
                  model.localDeleteTables([folderModel.id])
                }
                afterTableDelete={(id) => model.localDeleteTables([id])}
                afterSectionRestore={(folderModel) =>
                  model.localDeleteSections([folderModel.id])
                }
                afterSectionFinallyDelete={(id) =>
                  model.localDeleteSections([id])
                }
              ></TrashContentGridController>
            </Box>
          </PageInnerContainer>
        </ColumnCenterFlex>
      </PagePaddingWrapper>

      <WarningModal
        mainText={t('warnings.delete.trashMain')}
        secondText={t('warnings.delete.trashSecond')}
        isOpen={isDeleteAll}
        onCancel={() => setIsDeleteAll(false)}
        onContinue={async () => {
          try {
            await model.deleteAll();
          } catch (e) {
            console.error(e);
          }
          setIsDeleteAll(false);
        }}
      ></WarningModal>

      {isRestoreAll && (
        <RestoreAllModal
          isOpen={isRestoreAll}
          handleClose={() => setIsRestoreAll(false)}
          initialFolderId={null}
          initialSectionId={null}
          onSubmit={async (params) => {
            try {
              await trashApi.restoreAll(params);
              setIsRestoreAll(false);
            } catch (e) {
              const err = e as AxiosError<{ detail?: string }>;
              console.error(e);

              if (
                err?.response?.status === 400 &&
                (err?.response?.data?.detail === BackErrors.EntityExists ||
                  err?.response?.data?.detail === BackErrors.SectionExists)
              ) {
                setIsDublicateError(true);
                return;
              }

              setIsRestoreAll(false);
              mobxStore.notificationsStore.errorMessage = t(
                'notifications.errors.default'
              );
            }
          }}
        ></RestoreAllModal>
      )}

      {isDublicateError && (
        <ErrorModal
          mainText="Ошибка действия"
          isOpen={true}
          onClose={() => setIsDublicateError(false)}
        >
          <Typography variant="f18" sx={{ fontWeight: 300 }}>
            Действие невозможно, так как приведет к&nbsp;появлению дублей
            по&nbsp;указанному адресу
          </Typography>
        </ErrorModal>
      )}
    </PageWrapper>
  );
});
